import React from 'react';
import Markdown from 'react-markdown';
import { Download, ExternalLink, FileText, Music, Video } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  InAppAnnouncement,
  InAppMediaItem,
  isInternalInAppActionUrl,
  isSafeInAppActionUrl,
  mediaTypeLabel,
} from '@/lib/notifications/inAppNotification';

const isSafeMediaUrl = (url: unknown): url is string =>
  typeof url === 'string' && /^https?:\/\//i.test(url.trim());

const markdownComponents = {
  a: ({ href, children, ...props }: React.ComponentPropsWithoutRef<'a'> & { href?: string }) =>
    isSafeInAppActionUrl(href) ? (
      <a
        {...props}
        href={href}
        target={href.startsWith('/') ? undefined : '_blank'}
        rel="noopener noreferrer"
        className="text-blue-600 underline underline-offset-2 hover:text-blue-700"
      >
        {children}
      </a>
    ) : (
      <span>{children}</span>
    ),
  img: ({ src, alt }: React.ComponentPropsWithoutRef<'img'>) =>
    isSafeMediaUrl(src) ? (
      <img
        src={src}
        alt={alt || ''}
        loading="lazy"
        className="my-2 max-h-72 w-full rounded-lg object-cover"
        onError={(event) => {
          event.currentTarget.style.display = 'none';
        }}
      />
    ) : null,
  pre: ({ children, ...props }: React.ComponentPropsWithoutRef<'pre'>) => (
    <pre {...props} className="overflow-x-auto rounded-md bg-gray-100 p-3 text-xs">
      {children}
    </pre>
  ),
} as const;

const MEDIA_ICON: Record<InAppMediaItem['type'], React.ComponentType<{ className?: string }>> = {
  image: FileText,
  video: Video,
  audio: Music,
  document: FileText,
};

const MediaAttachment: React.FC<{ item: InAppMediaItem }> = ({ item }) => {
  if (!isSafeMediaUrl(item.url)) return null;

  if (item.type === 'image') {
    return (
      <figure className="overflow-hidden rounded-lg border border-gray-100">
        <img
          src={item.url}
          alt={item.caption || item.fileName || ''}
          loading="lazy"
          className="max-h-72 w-full object-cover"
          onError={(event) => {
            event.currentTarget.style.display = 'none';
          }}
        />
        {item.caption ? (
          <figcaption className="px-3 py-2 text-xs text-muted-foreground">{item.caption}</figcaption>
        ) : null}
      </figure>
    );
  }

  if (item.type === 'video') {
    return (
      <figure className="overflow-hidden rounded-lg border border-gray-100">
        <video
          src={item.url}
          poster={item.thumbnailUrl || undefined}
          controls
          preload="metadata"
          className="max-h-72 w-full bg-black"
        />
        {item.caption ? (
          <figcaption className="px-3 py-2 text-xs text-muted-foreground">{item.caption}</figcaption>
        ) : null}
      </figure>
    );
  }

  if (item.type === 'audio') {
    return (
      <div className="rounded-lg border border-gray-100 p-3">
        <audio src={item.url} controls preload="metadata" className="w-full" />
        {item.caption ? <p className="mt-2 text-xs text-muted-foreground">{item.caption}</p> : null}
      </div>
    );
  }

  const Icon = MEDIA_ICON[item.type] || FileText;
  return (
    <a
      href={item.url}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-3 rounded-lg border border-gray-100 p-3 transition-colors hover:bg-gray-50"
    >
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-gray-100">
        <Icon className="h-4 w-4 text-gray-600" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium text-foreground">
          {item.fileName || item.caption || mediaTypeLabel(item.type)}
        </span>
        <span className="block truncate text-xs text-muted-foreground">{item.url}</span>
      </span>
      <Download className="h-4 w-4 shrink-0 text-muted-foreground" />
    </a>
  );
};

export const InAppNotificationContent: React.FC<{
  announcement: InAppAnnouncement;
  className?: string;
  compact?: boolean;
  onNavigate?: (url: string) => void;
}> = ({ announcement, className, compact = false, onNavigate }) => {
  const { body, bodyFormat, media, actionButtons } = announcement;

  return (
    <div className={cn('space-y-3', className)} data-testid="in-app-notification-content">
      {body.trim() ? (
        bodyFormat === 'markdown' ? (
          <div className="prose prose-sm max-w-none break-words text-foreground prose-headings:mb-1 prose-headings:mt-3 prose-p:my-2">
            <Markdown components={markdownComponents}>{body}</Markdown>
          </div>
        ) : (
          <p className="whitespace-pre-wrap break-words text-sm text-foreground">{body}</p>
        )
      ) : null}

      {!compact && media.length > 0 ? (
        <div className="space-y-2" data-testid="in-app-notification-media">
          {media.map((item) => (
            <MediaAttachment key={item.url} item={item} />
          ))}
        </div>
      ) : null}

      {actionButtons?.length ? (
        <div className="flex flex-wrap gap-2 pt-1" data-testid="in-app-notification-actions">
          {actionButtons.map((button) => {
            const internal = isInternalInAppActionUrl(button.url);
            if (internal && onNavigate) {
              return (
                <Button
                  key={button.id}
                  type="button"
                  size="sm"
                  variant="outline"
                  className="rounded-[8px]"
                  onClick={() => onNavigate(button.url)}
                >
                  {button.label}
                </Button>
              );
            }
            return (
              <Button
                key={button.id}
                asChild
                size="sm"
                variant="outline"
                className="rounded-[8px]"
              >
                <a
                  href={button.url}
                  {...(internal ? {} : { target: '_blank', rel: 'noopener noreferrer' })}
                >
                  {button.label}
                  {!internal && <ExternalLink className="ml-1.5 h-3 w-3" />}
                </a>
              </Button>
            );
          })}
        </div>
      ) : null}
    </div>
  );
};

export default InAppNotificationContent;
